import {
    CustomFieldDefinition,
    CustomFieldType,
    UserCustomFieldValue,
    CustomFieldFormValues,
    UpdateUserCustomFieldsRequest,
} from '../types/customFields';

/**
 * Utility functions for working with custom fields
 */

// Convert form values to API request format
export const convertFormValuesToApiRequest = (
    formValues: CustomFieldFormValues,
    definitions: CustomFieldDefinition[]
): UpdateUserCustomFieldsRequest => {
    const values = Object.entries(formValues)
        .filter(([fieldDefinitionId, value]) => {
            // Only include fields that have values
            return value !== undefined && value !== null && value !== '';
        })
        .map(([fieldDefinitionId, value]) => {
            const definition = definitions.find((d) => d.id.toString() === fieldDefinitionId);

            return {
                fieldDefinitionId: parseInt(fieldDefinitionId),
                value: formatValueForApi(value, definition?.fieldType),
            };
        });

    return { values };
};

// Format value based on field type for API submission
export const formatValueForApi = (value: any, fieldType?: CustomFieldType): string | null => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    switch (fieldType) {
        case CustomFieldType.BOOLEAN:
            return Boolean(value).toString();

        case CustomFieldType.DATE:
            // Handle Date objects or ISO strings
            if (value instanceof Date) {
                return value.toISOString();
            }
            return String(value);

        case CustomFieldType.NUMBER:
        case CustomFieldType.DECIMAL:
            return String(value);

        case CustomFieldType.SELECT:
        case CustomFieldType.TEXT:
        default:
            return String(value);
    }
};

// Convert user field values to form initial values
export const convertUserFieldsToFormValues = (
    userFields: UserCustomFieldValue[],
    definitions: CustomFieldDefinition[]
): CustomFieldFormValues => {
    const formValues: CustomFieldFormValues = {};

    // Initialize all available fields
    definitions.forEach((definition) => {
        const userField = userFields.find((uf) => uf.fieldName === definition.name);

        if (userField && userField.value !== null) {
            formValues[definition.id.toString()] = parseValueFromApi(userField.value, definition.fieldType);
        } else {
            // Set default values for empty fields
            formValues[definition.id.toString()] = getDefaultValueForFieldType(definition.fieldType);
        }
    });

    return formValues;
};

// Parse value from API based on field type
export const parseValueFromApi = (value: string | null, fieldType: CustomFieldType): any => {
    if (value === null || value === undefined) {
        return getDefaultValueForFieldType(fieldType);
    }

    switch (fieldType) {
        case CustomFieldType.NUMBER:
            return parseInt(value) || 0;

        case CustomFieldType.DECIMAL:
            return parseFloat(value) || 0;

        case CustomFieldType.BOOLEAN:
            return value === 'true';

        case CustomFieldType.DATE:
            return value ? new Date(value) : null;

        case CustomFieldType.SELECT:
        case CustomFieldType.TEXT:
        default:
            return value;
    }
};

// Get default value for field type
export const getDefaultValueForFieldType = (fieldType: CustomFieldType): any => {
    switch (fieldType) {
        case CustomFieldType.NUMBER:
        case CustomFieldType.DECIMAL:
            return '';

        case CustomFieldType.BOOLEAN:
            return false;

        case CustomFieldType.DATE:
            return null;

        case CustomFieldType.SELECT:
            return '';

        case CustomFieldType.TEXT:
        default:
            return '';
    }
};

// Get currency symbol for rate fields
export const getCurrencySymbol = (fieldName: string): string => {
    if (fieldName.includes('RATE')) {
        return '€';
    }
    return '';
};
